import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router"
import type { PublicGuidebookPayload } from "../guidebook/types"
import { fetchPublicGuidebookBySlug } from "../guidebook/api"
import { getVoiceCopy } from "../house-voice-copy"
import { useSlideEngine } from "./useSlideEngine"
import { useWakeLock } from "./useWakeLock"
import { useTvNightMode } from "./useTvNightMode"
import { buildSlides, type TvDisplayMode } from "./tvAdapter"
import { HeroSlide } from "./slides/HeroSlide"
import { TodaySlide } from "./slides/TodaySlide"
import { CuratedPlaceSlide } from "./slides/CuratedPlaceSlide"
import { EssentialsSlide } from "./slides/EssentialsSlide"
import { weatherEmoji } from "./tvUtils"
import "./tv-display.css"

function parseMode(value: string | null): TvDisplayMode {
  if (value === "complement" || value === "ambient") return value
  return "standalone"
}

function clampDuration(value: string | null): number {
  const n = Number(value)
  if (!Number.isFinite(n)) return 20
  return Math.max(15, Math.min(30, Math.round(n)))
}

export function TvDisplayPage() {
  const { slug = "" } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const mode = parseMode(searchParams.get("mode"))
  const duration = clampDuration(searchParams.get("duration"))

  const [data, setData] = useState<PublicGuidebookPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const normalizedSlug = useMemo(() => String(slug || "").trim().toLowerCase(), [slug])

  // Fetch guidebook data
  const loadData = useCallback(async () => {
    if (!normalizedSlug) return
    try {
      const result = await fetchPublicGuidebookBySlug(normalizedSlug)
      setData(result)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data")
    } finally {
      setLoading(false)
    }
  }, [normalizedSlug])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Resolve voice copy once
  const voiceCopy = useMemo(() => getVoiceCopy(data?.house_voice), [data?.house_voice])

  // Build slides from data (between-bookings now handled inside adapter)
  const slides = useMemo(() => {
    if (!data) return []
    return buildSlides(data, mode, data.tv_slide_config)
  }, [data, mode])

  // Slide engine
  const { currentSlide, phase, currentIndex, total, goNext, goPrev, togglePause, isPaused } = useSlideEngine({
    slides,
    defaultDuration: duration,
    onRefreshRequest: loadData,
    refreshIntervalMs: 30 * 60 * 1000,
  })

  // Wake lock
  useWakeLock()

  // Night mode
  const timezone = data?.weather?.timezone || null
  const nightModeConfig = data?.night_mode
  const { active: nightActive } = useTvNightMode({
    enabled: nightModeConfig?.enabled ?? false,
    startHour: nightModeConfig?.start_hour ?? 22,
    endHour: nightModeConfig?.end_hour ?? 6,
    timezone,
  })

  // Slide dots visibility — show for 5s after any key press
  const [dotsVisible, setDotsVisible] = useState(false)
  const dotsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Keyboard handler for remote control
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowRight":
          e.preventDefault()
          goNext()
          break
        case "ArrowLeft":
          e.preventDefault()
          goPrev()
          break
        case "Enter":
        case " ":
        case "MediaPlayPause":
          e.preventDefault()
          togglePause()
          break
      }
      setDotsVisible(true)
      if (dotsTimerRef.current) clearTimeout(dotsTimerRef.current)
      dotsTimerRef.current = setTimeout(() => setDotsVisible(false), 5000)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      if (dotsTimerRef.current) clearTimeout(dotsTimerRef.current)
    }
  }, [goNext, goPrev, togglePause])

  // Popstate guard
  useEffect(() => {
    window.history.pushState(null, "", window.location.href)
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href)
    }
    window.addEventListener("popstate", handlePopState)
    return () => {
      window.removeEventListener("popstate", handlePopState)
    }
  }, [])

  // Auto-hide cursor
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    const show = () => {
      document.body.style.cursor = "default"
      clearTimeout(timer)
      timer = setTimeout(() => {
        document.body.style.cursor = "none"
      }, 3000)
    }
    document.addEventListener("mousemove", show)
    timer = setTimeout(() => {
      document.body.style.cursor = "none"
    }, 3000)
    return () => {
      clearTimeout(timer)
      document.removeEventListener("mousemove", show)
      document.body.style.cursor = "default"
    }
  }, [])

  // Loading state
  if (loading) {
    return (
      <div className="tv-display tv-display--loading">
        <div className="tv-loading">
          <div className="tv-loading__spinner" />
        </div>
      </div>
    )
  }

  // Error state
  if (error || !data) {
    return (
      <div className="tv-display tv-display--error">
        <div className="tv-error">
          <p className="tv-error__message">{error || "Unable to load display"}</p>
          <p className="tv-error__hint">Check the property URL and try again</p>
        </div>
      </div>
    )
  }

  // Night mode overlay
  if (nightActive) {
    const now = new Date()
    let timeStr: string
    try {
      timeStr = now.toLocaleTimeString("en-GB", {
        timeZone: timezone || undefined,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    } catch {
      timeStr = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })
    }
    const emoji = data.weather ? weatherEmoji(data.weather.weather_code, data.weather.is_day) : ""
    const temp = data.weather?.temperature_c != null ? `${Math.round(data.weather.temperature_c)}°` : ""

    return (
      <div className="tv-display tv-display--night">
        <div className="tv-night">
          <span className="tv-night__time">{timeStr}</span>
          {temp && (
            <span className="tv-night__weather">
              {emoji} {temp}
            </span>
          )}
        </div>
      </div>
    )
  }

  // No slides
  if (!currentSlide) {
    return (
      <div className="tv-display">
        <div className="tv-error">
          <p className="tv-error__message">No content available</p>
        </div>
      </div>
    )
  }

  return (
    <div className="tv-display">
      <div className={`tv-slide ${phase === "visible" ? "tv-slide--active" : ""}`}>
        {renderSlide(currentSlide.type, currentSlide.data, voiceCopy)}
      </div>

      {/* Pause indicator */}
      <div className={`tv-pause-indicator ${isPaused ? "tv-pause-indicator--visible" : ""}`}>
        ⏸ Paused
      </div>

      {/* Slide position dots */}
      {total > 1 && (
        <div className={`tv-slide-dots ${dotsVisible || isPaused ? "tv-slide-dots--visible" : ""}`}>
          {slides.map((_, i) => (
            <div
              key={i}
              className={`tv-slide-dot ${i === currentIndex ? "tv-slide-dot--active" : ""}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function renderSlide(type: string, data: Record<string, unknown>, voiceCopy?: { tv_hero_stay_line: string; tv_hero_checkout_nudge: string }) {
  switch (type) {
    case "hero":
      return (
        <HeroSlide
          coverImageUrl={data.coverImageUrl as string | null}
          propertyName={data.propertyName as string}
          guestName={(data.guestName as string) || null}
          checkoutDayOfWeek={(data.checkoutDayOfWeek as string) || null}
          checkinTime={(data.checkinTime as string) || null}
          checkoutTime={(data.checkoutTime as string) || null}
          wifiName={(data.wifiName as string) || null}
          weatherLine={(data.weatherLine as string) || null}
          bookingPhase={(data.bookingPhase as string) || null}
          stayLineTemplate={voiceCopy?.tv_hero_stay_line}
          checkoutNudge={voiceCopy?.tv_hero_checkout_nudge}
        />
      )
    case "today":
      return (
        <TodaySlide
          areaName={data.areaName as string}
          weatherNarrative={data.weatherNarrative as string}
          highlights={data.highlights as Array<{ emoji: string; label: string; motivation: string }>}
        />
      )
    case "curated_place":
      return (
        <CuratedPlaceSlide
          name={data.name as string}
          shortDescription={(data.shortDescription as string) || null}
          tip={(data.tip as string) || null}
        />
      )
    case "essentials":
      return (
        <EssentialsSlide
          wifiName={(data.wifiName as string) || null}
          wifiPassword={(data.wifiPassword as string) || null}
          checkoutTime={(data.checkoutTime as string) || null}
          quietHoursLine={(data.quietHoursLine as string) || null}
          compactRules={(data.compactRules as string[]) || []}
        />
      )
    default:
      return null
  }
}
